/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  TrecClinicalDataCustom,
  TrecReminder,
  TrecTask,
  TrecTherapy,
} from "@trec/api/clinical-data";
import {RangeDatesUnix} from "@trec/api/common";
import {TrecApiError, TrecError} from "@trec/api/errors";
import {ErrorAction, StoreAction} from "@trec/components/store";

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace TpBaseActions {
  // Search therapy template groups ///////////////////////////////////////////////////////////////

  export class SearchTherapyTemplateGroups extends StoreAction {
    static readonly type = `[Treatment Plan Base] Search therapy template groups`;

    /**
     * @param payload.query Partial string to use as search query.
     */
    constructor(public payload: {query: string}) {
      super();
    }
  }

  export class SearchTherapyTemplateGroupsError extends ErrorAction {
    static readonly type = `[Treatment Plan Base] Search therapy template groups error`;
    readonly error: true = true;

    constructor(public payload: TrecApiError) {
      super();
    }
  }

  // Therapies //////////////////////////////////////////////////////////////////////////////////////

  export class FetchTherapies extends StoreAction {
    static readonly type = `[Treatment Plan Base] Fetch therapies`;
  }

  export class InsertTherapy extends StoreAction {
    static readonly type = `[Treatment Plan Base] Insert therapies`;

    constructor(public payload: {therapies: TrecTherapy[]}) {
      super();
    }
  }

  export class EditTherapy extends StoreAction {
    static readonly type = `[Treatment Plan Base] Edit therapies`;

    constructor(public payload: {therapies: TrecTherapy[]}) {
      super();
    }
  }

  export class InterruptTherapy extends StoreAction {
    static readonly type = `[Treatment Plan Base] Interrupt therapy`;

    constructor(public payload: {therapy: TrecTherapy; date: number; structure: string; doctorId: string; doctorName: string}) {
      super();
    }
  }

  export class SuspendTherapy extends StoreAction {
    static readonly type = `[Treatment Plan Base] Suspend therapy`;

    constructor(public payload: {therapy: TrecTherapy; date: number; structure: string; doctorId: string; doctorName: string}) {
      super();
    }
  }

  export class ResumeTherapy extends StoreAction {
    static readonly type = `[Treatment Plan Base] Resume therapy`;

    constructor(public payload: {therapy: TrecTherapy; date: number; structure: string; doctorId: string; doctorName: string}) {
      super();
    }
  }

  // Glycaemia profile //////////////////////////////////////////////////////////////////////////////

  export class FetchGlycaemiaProfile extends StoreAction {
    static readonly type = `[Treatment Plan Base] Fetch glycaemia profile`;
  }

  export class UpdateGlycaemiaProfile extends StoreAction {
    static readonly type = `[Treatment Plan Base] Update glycaemia profile`;

    constructor(public payload: {glycaemiaProfile: TrecClinicalDataCustom}) {
      super();
    }
  }

  // Examinations ///////////////////////////////////////////////////////////////////////////////////

  export class FetchExaminations extends StoreAction {
    static readonly type = `[Treatment Plan Base] Fetch examinations`;
  }

  export class InsertExamination extends StoreAction {
    static readonly type = `[Treatment Plan Base] Insert Examinations`;

    constructor(public payload: {examinations: TrecReminder[]}) {
      super();
    }
  }

  export class EditExamination extends StoreAction {
    static readonly type = `[Treatment Plan Base] Edit Examination`;

    constructor(public payload: {examination: TrecReminder}) {
      super();
    }
  }

  export class DeleteExamination extends StoreAction {
    static readonly type = `[Treatment Plan Base] Delete Examination`;

    constructor(public payload: {examination: TrecReminder}) {
      super();
    }
  }

  // Tasks //////////////////////////////////////////////////////////////////////////////////////////

  export class FetchTasks extends StoreAction {
    static readonly type = `[Treatment Plan Base] Fetch tasks`;

    constructor(public payload: {diaryPeriod?: RangeDatesUnix}) {
      super();
    }
  }

  export class InsertTask extends StoreAction {
    static readonly type = `[Treatment Plan Base] Insert task`;

    constructor(public payload: {task: TrecTask}) {
      super();
    }
  }

  export class EditTask extends StoreAction {
    static readonly type = `[Treatment Plan Base] Edit task`;

    constructor(public payload: {task: TrecTask}) {
      super();
    }
  }

  export class DeleteTask extends StoreAction {
    static readonly type = `[Treatment Plan Base] Delete task`;

    constructor(public payload: {task: TrecTask}) {
      super();
    }
  }

  export class TpBaseError extends ErrorAction {
    static readonly type = `[Treatment Plan Base] Error`;
    readonly error: true = true;

    readonly errorMsgs = {
      illegal_operation: "Questa operazione non può essere effettuata o è già stata eseguita",
    };

    constructor(public payload: TrecError | TrecApiError) {
      super();
    }
  }

  // Clear status ///////////////////////////////////////////////////////////////////////////////////

  export class ClearTherapyTemplateGroupSearchResultsList extends StoreAction {
    static readonly type = `[Treatment Plan Base] Clear therapy template groups search result list`;
  }
  export class ClearStatus extends StoreAction {
    static readonly type = `[Treatment Plan Base] Clear status`;
  }
}
